/**
 * Story 29.3 — TanStack hooks for Settings › Notifications.
 *
 * Query key `['notification-settings', tenantId]` (the tenant scopes the
 * cache). `enabled` lets the owner-only banner skip the request entirely for
 * members and viewers. Both mutations invalidate the key on success, so the
 * page and the banner re-read the same answer.
 *
 * Story 30.5 — `useQuietHours` / `useSaveQuietHours`, keyed
 * `['quiet-hours', tenantId, userId]`: quiet hours are the signed-in
 * person's own, so a different person on the same browser never sees a
 * cached answer.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotificationSettings,
  fetchQuietHours,
  putNotificationContact,
  putPushPaused,
  putQuietHours,
  type NotificationSettings,
  type QuietHours,
  type QuietHoursInput,
} from '@/lib/api/notificationSettings';
import { useAuth } from '@/lib/contexts/AuthContext';

/** Key prefix — invalidate it to refetch every tenant's entry. */
export const NOTIFICATION_SETTINGS_QUERY_KEY = ['notification-settings'] as const;

export function useNotificationSettings(
  options: { enabled?: boolean; skipErrorToast?: boolean } = {},
) {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? null;
  const enabled = (options.enabled ?? true) && tenantId !== null;
  return useQuery<NotificationSettings, Error>({
    queryKey: [...NOTIFICATION_SETTINGS_QUERY_KEY, tenantId],
    queryFn: ({ signal }) =>
      fetchNotificationSettings(signal, { skipErrorToast: options.skipErrorToast }),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useSetNotificationContact() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { scope: string; userId: string | null }>({
    mutationFn: ({ scope, userId }) => putNotificationContact(scope, userId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_SETTINGS_QUERY_KEY }),
  });
}

export function useSetPushPaused() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { paused: boolean }>({
    mutationFn: ({ paused }) => putPushPaused(paused),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_SETTINGS_QUERY_KEY }),
  });
}

/** Key prefix for the signed-in person's quiet hours. */
export const QUIET_HOURS_QUERY_KEY = ['quiet-hours'] as const;

export function useQuietHours() {
  const { tenant, user } = useAuth();
  const tenantId = tenant?.id ?? null;
  const userId = user?.id ?? null;
  return useQuery<QuietHours, Error>({
    queryKey: [...QUIET_HOURS_QUERY_KEY, tenantId, userId],
    queryFn: ({ signal }) => fetchQuietHours(signal),
    enabled: tenantId !== null,
    staleTime: 60_000,
    retry: false,
  });
}

export function useSaveQuietHours() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, QuietHoursInput>({
    mutationFn: (input) => putQuietHours(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUIET_HOURS_QUERY_KEY }),
  });
}
